import React, { useState, useEffect } from 'react';
import {
  Modal,
  Button,
  Row,
  Col,
  Card,
  Badge,
  Image,
  Alert,
  Spinner
} from 'react-bootstrap';
import axios from 'axios';

function SetRosterManagerModal({ show, onHide, teamPlayers, onSave, gameId, teamId, isCommissioner }) {
  const [rosterOrder, setRosterOrder] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [existingRoster, setExistingRoster] = useState(null);
  const [isRosterFinal, setIsRosterFinal] = useState(false);

  useEffect(() => {
    if (show && teamPlayers.length > 0) {
      setRosterOrder([...teamPlayers]);
      fetchExistingRoster();
    }
  }, [show, teamPlayers]);

  const fetchExistingRoster = async () => {
    if (!gameId || !teamId) return;

    setLoading(true);
    try {
      const response = await axios.get(
        `/api/games/${gameId}/teams/${teamId}/roster/`,
        { withCredentials: true }
      );

      if (response.data.ordered_player_ids_read && response.data.ordered_player_ids_read.length > 0) {
        // Map the roster order to player objects
        const playerMap = {};
        teamPlayers.forEach(player => {
          playerMap[player.id] = player;
        });

        const existingRosterOrder = response.data.ordered_player_ids_read
          .map(id => playerMap[id])
          .filter(player => player); // Filter out any missing players

        setRosterOrder(existingRosterOrder);
        setExistingRoster(response.data);
        setIsRosterFinal(response.data.is_final || false);
      }
    } catch (error) {
      console.error('Error fetching existing roster:', error);
      setError('Failed to load existing roster');
    } finally {
      setLoading(false);
    }
  };

  const handleUnlockRoster = async () => {
    if (!isCommissioner) return;

    try {
      // Call the backend to unlock the roster
      await axios.post(
        `/api/games/${gameId}/teams/${teamId}/roster/unlock/`,
        {},
        { withCredentials: true }
      );

      setIsRosterFinal(false);
      setError(null);
    } catch (error) {
      console.error('Error unlocking roster:', error);
      setError('Failed to unlock roster');
    }
  };

  const handleSave = async () => {
    if (rosterOrder.length === 0) return;

    setSaving(true);
    try {
      await onSave(rosterOrder);
      setSaving(false);
    } catch (error) {
      console.error('Error saving roster:', error);
      setError('Failed to save roster');
      setSaving(false);
    }
  };

  const handlePlayerMove = (fromIndex, toIndex) => {
    const newOrder = [...rosterOrder];
    const [movedPlayer] = newOrder.splice(fromIndex, 1);
    newOrder.splice(toIndex, 0, movedPlayer);
    setRosterOrder(newOrder);
  };

  const handlePlayerRemove = (index) => {
    const newOrder = rosterOrder.filter((_, i) => i !== index);
    setRosterOrder(newOrder);
  };

  const handlePlayerAdd = (player) => {
    if (!rosterOrder.find(p => p.id === player.id)) {
      setRosterOrder([...rosterOrder, player]);
    }
  };

  const availablePlayers = teamPlayers.filter(player =>
    !rosterOrder.find(p => p.id === player.id)
  );

  return (
    <Modal show={show} onHide={onHide} size="xl">
      <Modal.Header closeButton>
        <Modal.Title>Manage Roster</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading ? (
          <div className="text-center py-4">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
            <p className="mt-2">Loading roster...</p>
          </div>
        ) : (
          <>
            {/* Roster Status and Unlock Button */}
            {isRosterFinal && isCommissioner && (
              <Alert variant="warning" className="mb-3">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <i className="bi bi-lock-fill me-2"></i>
                    This roster is final and locked. You can unlock it to make changes.
                  </div>
                  <Button
                    variant="outline-warning"
                    size="sm"
                    onClick={handleUnlockRoster}
                  >
                    <i className="bi bi-unlock me-1"></i>
                    Unlock Roster
                  </Button>
                </div>
              </Alert>
            )}

            <Row>
              {/* Current Roster */}
              <Col md={8}>
                <Card>
                  <Card.Header>
                    <h6 className="mb-0">Current Batting Order ({rosterOrder.length})</h6>
                  </Card.Header>
                  <Card.Body>
                    {rosterOrder.length === 0 ? (
                      <p className="text-muted">No players in roster. Add players from the available list.</p>
                    ) : (
                      <div className="roster-list">
                        {rosterOrder.map((player, index) => (
                          <div key={`${player.id}-${index}`} className="d-flex align-items-center p-2 border rounded mb-2">
                            <div className="me-3">
                              <Badge bg="primary">{index + 1}</Badge>
                            </div>
                            <Image
                              src={player.profile_picture || `/static/images/kb_static/lemont_rec_leagues_circle.png`}
                              alt={`${player.first_name} ${player.last_name}`}
                              roundedCircle
                              style={{ width: '40px', height: '40px' }}
                              className="me-3"
                            />
                            <div className="flex-grow-1">
                              <h6 className="mb-0">{player.first_name} {player.last_name}</h6>
                              <div className="d-flex align-items-center gap-1">
                                <Badge
                                  bg={player.gender === 'M' ? 'dark' : 'danger'}
                                  className="small"
                                  style={{ fontSize: '0.7rem' }}
                                >
                                  {player.gender === 'M' ? 'M' : 'W'}
                                </Badge>
                                <small className="text-muted">{player.email}</small>
                              </div>
                            </div>
                            <div className="d-flex gap-1">
                              {isCommissioner && index > 0 && (
                                <Button
                                  size="sm"
                                  variant="outline-secondary"
                                  onClick={() => handlePlayerMove(index, index - 1)}
                                >
                                  ↑
                                </Button>
                              )}
                              {isCommissioner && index < rosterOrder.length - 1 && (
                                <Button
                                  size="sm"
                                  variant="outline-secondary"
                                  onClick={() => handlePlayerMove(index, index + 1)}
                                >
                                  ↓
                                </Button>
                              )}
                              {isCommissioner && (
                                <Button
                                  size="sm"
                                  variant="outline-danger"
                                  onClick={() => handlePlayerRemove(index)}
                                >
                                  ×
                                </Button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </Card.Body>
                </Card>
              </Col>

              {/* Available Players */}
              <Col md={4}>
                <Card>
                  <Card.Header>
                    <h6 className="mb-0">Available Players ({availablePlayers.length})</h6>
                  </Card.Header>
                  <Card.Body>
                    {availablePlayers.length === 0 ? (
                      <p className="text-muted">All players are in the roster.</p>
                    ) : (
                      <div className="available-players">
                        {availablePlayers.map((player) => (
                          <div key={`available-${player.id}`} className="d-flex align-items-center p-2 border rounded mb-2">
                            <Image
                              src={player.profile_picture || `/static/images/kb_static/lemont_rec_leagues_circle.png`}
                              alt={`${player.first_name} ${player.last_name}`}
                              roundedCircle
                              style={{ width: '30px', height: '30px' }}
                              className="me-2"
                            />
                            <div className="flex-grow-1">
                              <h6 className="mb-0 small">{player.first_name} {player.last_name}</h6>
                              <Badge
                                bg={player.gender === 'M' ? 'dark' : 'danger'}
                                className="small"
                                style={{ fontSize: '0.6rem' }}
                              >
                                {player.gender === 'M' ? 'M' : 'W'}
                              </Badge>
                            </div>
                            {!isRosterFinal && (
                              <Button
                                size="sm"
                                variant="outline-success"
                                onClick={() => handlePlayerAdd(player)}
                              >
                                +
                              </Button>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </>
        )}

        {error && (
          <Alert variant="danger" className="mt-3">
            {error}
          </Alert>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Cancel
        </Button>
        {isCommissioner && (
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={saving || rosterOrder.length === 0}
          >
            {saving ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Saving...
              </>
            ) : (
              'Save Roster'
            )}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
}

export default SetRosterManagerModal; 